'use client';

import type { Theme, SxProps } from '@mui/material/styles';

import { m } from 'framer-motion';

import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import { ForbiddenIllustration } from 'src/assets/illustrations';

import { varBounce, MotionContainer } from 'src/components/animate';

import { useAuthContext } from '../hooks/use-auth-context';

// ----------------------------------------------------------------------

/**
 * Phase 24 — permission-based access gate.
 *
 * Reads `user.permissions` (resolved server-side from `role_permissions`
 * and embedded in the JWT) from the auth context and renders the forbidden
 * page when the current user lacks the required permission codes.
 *
 * The legacy `currentRole` + `allowedRoles` API is preserved for the
 * Minimals CC demo at `src/sections/permission/view.tsx`, which toggles a
 * synthetic local role and is not wired to real auth. New code should use
 * `requiredPermissions` (with optional `match`).
 */

type CommonProps = {
  sx?: SxProps<Theme>;
  hasContent?: boolean;
  children: React.ReactNode;
};

type PermissionModeProps = CommonProps & {
  /** Required permission code(s). If empty, the gate is a no-op. */
  requiredPermissions: string | string[];
  /**
   * `'any'` (default) — user must have at least one of `requiredPermissions`.
   * `'all'` — user must have every code in `requiredPermissions`.
   */
  match?: 'any' | 'all';
  /** Not used in permission mode; accepted for backward compatibility. */
  currentRole?: never;
  allowedRoles?: never;
};

type LegacyRoleModeProps = CommonProps & {
  /**
   * Synthetic role string compared against `allowedRoles`. Only used by
   * the Minimals CC permission demo — production code paths should use
   * `requiredPermissions`.
   */
  currentRole: string;
  allowedRoles: string | string[];
  requiredPermissions?: never;
  match?: never;
};

export type RoleBasedGuardProp = PermissionModeProps | LegacyRoleModeProps;

function ForbiddenContent({ sx }: { sx?: SxProps<Theme> }) {
  return (
    <Container
      component={MotionContainer}
      sx={[{ textAlign: 'center' }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      <m.div variants={varBounce('in')}>
        <Typography variant="h3" sx={{ mb: 2 }}>
          Permission denied
        </Typography>
      </m.div>

      <m.div variants={varBounce('in')}>
        <Typography sx={{ color: 'text.secondary' }}>
          You do not have permission to access this page.
        </Typography>
      </m.div>

      <m.div variants={varBounce('in')}>
        <ForbiddenIllustration sx={{ my: { xs: 5, sm: 10 } }} />
      </m.div>
    </Container>
  );
}

export function RoleBasedGuard(props: RoleBasedGuardProp) {
  const { children, hasContent, sx } = props;
  const { permissions } = useAuthContext();

  // Permission mode (new — Phase 24).
  if ('requiredPermissions' in props && props.requiredPermissions !== undefined) {
    const required = Array.isArray(props.requiredPermissions)
      ? props.requiredPermissions
      : [props.requiredPermissions];
    const match = props.match ?? 'any';

    if (required.length === 0) {
      return <>{children}</>;
    }

    const allowed =
      match === 'all'
        ? required.every((p) => permissions.includes(p))
        : required.some((p) => permissions.includes(p));

    if (!allowed) {
      return hasContent ? <ForbiddenContent sx={sx} /> : null;
    }
    return <>{children}</>;
  }

  // Legacy role mode — kept for the Minimals CC permission demo.
  if ('allowedRoles' in props && props.allowedRoles !== undefined) {
    const { currentRole, allowedRoles } = props;
    if (currentRole && allowedRoles && !allowedRoles.includes(currentRole)) {
      return hasContent ? <ForbiddenContent sx={sx} /> : null;
    }
  }

  return <>{children}</>;
}

/**
 * Alias preferred for new code. Identical to `RoleBasedGuard` but signals
 * intent — gated by JWT permission codes, not role strings.
 */
export const PermissionGuard = RoleBasedGuard;
